import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Search, Check, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { inr, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { canWrite } from "@/nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Panel } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { VerticalStepper, buildSteps } from "@/components/Stepper";
import { FieldInput } from "@/components/FieldInput";
import { FormFields, compact, FORM_DIALOG } from "@/components/RecordDialog";
import { DetailGrid } from "@/components/PageHeader";

const RA_FIELDS = [
  { section: "Bill details", name: "site", label: "Site", type: "select", required: true, options: ["My Home Avali", "My Home Akrida", "Tellapur Technocity"] },
  { section: "Bill details", name: "contractor", label: "Contractor", type: "ref", ref: "vendors", required: true },
  { section: "Bill details", name: "work_order_ref", label: "Work Order Ref" },
  { section: "Bill details", name: "ra_sequence", label: "RA Bill Sequence (e.g. RA-05)" },
  { section: "Bill details", name: "bill_period_from", label: "Bill Period From", type: "date" },
  { section: "Bill details", name: "bill_period_to", label: "Bill Period To", type: "date" },
  { section: "Bill details", name: "mb_ref", label: "Measurement Book Ref" },
  { section: "Amounts", name: "gross_value", label: "Gross Work Done (₹)", type: "currency" },
  { section: "Amounts", name: "previous_billed", label: "Previously Billed (₹)", type: "currency" },
  { section: "Amounts", name: "retention_pct", label: "Retention (%)", type: "number" },
  { section: "Amounts", name: "tds_pct", label: "TDS (%)", type: "number" },
  { section: "Amounts", name: "advance_recovery", label: "Advance Recovery (₹)", type: "currency" },
  { section: "Amounts", name: "amount", label: "Net Bill Amount (₹)", type: "currency", required: true },
  { section: "Amounts", name: "description", label: "Description of Work", type: "textarea" },
];

const SITES = ["My Home Avali", "My Home Akrida", "Tellapur Technocity"];

const billStageLabel = (b) => {
  if (b.status === "posted") return "SAP Posted";
  if (b.status === "returned") return "Returned · Site QS";
  return b.stage_name;
};

const FilterSelect = ({ value, onChange, all, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="sm:w-48" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{all}</SelectItem>
      {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

const NewBillDialog = ({ open, onOpenChange, onCreated }) => {
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF({}); }, [open]);
  const save = async () => {
    try {
      const { data } = await api.post("/ra-bills", compact(f));
      toast.success(`${data.bill_no} created`);
      onCreated(data);
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-new-ra-bill" className={FORM_DIALOG}>
        <DialogHeader><DialogTitle className="font-heading">New RA Bill</DialogTitle></DialogHeader>
        <FormFields fields={RA_FIELDS} form={f} setForm={setF} />
        <DialogFooter><Button onClick={save} data-testid="btn-create-ra-bill">Create bill</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BillPanel = ({ bill, stages, user, onUpdated }) => {
  const [note, setNote] = useState("");
  const [notes, setNotes] = useState(bill.notes || "");
  useEffect(() => { setNote(""); setNotes(bill.notes || ""); }, [bill.id, bill.notes]);
  const posted = bill.status === "posted";
  const cur = stages[bill.stage];
  const canAct = !posted && (user.role === "admin" || user.role === cur.role);
  const act = async (action) => {
    try {
      const { data } = await api.post(`/ra-bills/${bill.id}/action`, { action, note });
      toast.success(action === "approve" ? `${bill.bill_no} approved · next approver notified by email` : `${bill.bill_no} returned to Site QS · notified by email`);
      onUpdated(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const saveNotes = async () => {
    try { onUpdated((await api.put(`/ra-bills/${bill.id}/notes`, { notes })).data); toast.success("Notes saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Selected bill" testId="selected-bill-panel" className="lg:sticky lg:top-20">
      <div className="space-y-5 p-4">
        <div>
          <p className="font-heading text-xl font-bold" data-testid="selected-bill-title">{bill.bill_no} · {posted ? "Posted" : "Approval Stage"}</p>
          <p className="text-sm text-slate-500">{bill.contractor} · {bill.site} · <span className="font-mono">{inr(bill.amount)}</span></p>
          {bill.status === "returned" && <StatusBadge value="Returned" className="mt-2" />}
          {bill.sap_doc && <p className="mt-1 font-mono text-xs text-emerald-700" data-testid="sap-doc">SAP Doc: {bill.sap_doc}</p>}
        </div>
        <DetailGrid testId="bill-details" items={[["Work order", bill.work_order_ref], ["RA sequence", bill.ra_sequence],
          ["Period", bill.bill_period_from && `${fmtDate(bill.bill_period_from)} – ${fmtDate(bill.bill_period_to)}`], ["MB ref", bill.mb_ref],
          ["Gross value", bill.gross_value != null ? inr(bill.gross_value) : ""], ["Prev. billed", bill.previous_billed != null ? inr(bill.previous_billed) : ""],
          ["Retention", bill.retention_pct != null ? `${bill.retention_pct}%` : ""], ["TDS", bill.tds_pct != null ? `${bill.tds_pct}%` : ""],
          ["Advance recovery", bill.advance_recovery != null ? inr(bill.advance_recovery) : ""], ["Description", bill.description]]} />
        <VerticalStepper steps={buildSteps(stages, bill, posted)} />
        {canAct && (
          <div className="space-y-2 rounded border border-amber-200 bg-amber-50/50 p-3">
            <Label className="text-xs">Remarks for {cur.name}</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="input-action-note" placeholder="Optional for approval, required to return" />
            <div className="flex gap-2">
              <Button className="flex-1" onClick={() => act("approve")} data-testid="btn-approve-bill"><Check className="mr-1.5 h-4 w-4" />{bill.stage === stages.length - 1 ? "Post to SAP" : "Approve & forward"}</Button>
              {bill.stage > 0 && <Button variant="outline" onClick={() => act("reject")} data-testid="btn-return-bill"><Undo2 className="mr-1.5 h-4 w-4" />Return</Button>}
            </div>
          </div>
        )}
        {!canAct && !posted && <p className="text-xs text-slate-500" data-testid="awaiting-role">Awaiting action by {cur.role_label}</p>}
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-slate-500">Notes</Label>
          {canWrite(user, "qs") ? (
            <>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="input-bill-notes" />
              <Button size="sm" variant="outline" onClick={saveNotes} data-testid="btn-save-notes">Save notes</Button>
            </>
          ) : <p className="text-sm text-slate-600" data-testid="bill-notes-readonly">{bill.notes || "-"}</p>}
        </div>
      </div>
    </Panel>
  );
};

export default function RABillTracker() {
  const { user } = useAuth();
  const [bills, setBills] = useState([]);
  const [stages, setStages] = useState([]);
  const [sel, setSel] = useState(null);
  const [site, setSite] = useState("all");
  const [stage, setStage] = useState("all");
  const [q, setQ] = useState("");
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(() => api.get("/ra-bills").then((r) => { setBills(r.data); setSel((s) => s || r.data.find((b) => b.status !== "posted")?.id || r.data[0]?.id); }), []);
  useEffect(() => { api.get("/ra-bills/meta").then((r) => setStages(r.data.stages)); load(); }, [load]);

  const filtered = useMemo(() => bills.filter((b) =>
    (site === "all" || b.site === site) && (stage === "all" || (stage === "SAP Posted" ? b.status === "posted" : b.stage_name === stage && b.status !== "posted")) &&
    (!q || `${b.bill_no} ${b.site} ${b.contractor}`.toLowerCase().includes(q.toLowerCase()))), [bills, site, stage, q]);
  const selected = bills.find((b) => b.id === sel);
  const onUpdated = (b) => setBills((bs) => bs.map((x) => (x.id === b.id ? b : x)));

  return (
    <div>
      <PageHeader dept="QS & Billing" title="RA Bill Approval Tracker" subtitle="Running account bills moving through Site QS → HOD → Sr. President → VC → SAP">
        {(user.role === "admin" || user.role === "site_qs") && <Button onClick={() => setNewOpen(true)} data-testid="btn-new-ra-bill"><Plus className="mr-1.5 h-4 w-4" />New RA Bill</Button>}
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-12">
        <Panel className="min-w-0 lg:col-span-8" testId="ra-bill-table">
          <div className="flex flex-col gap-2 border-b border-slate-200 p-3 sm:flex-row">
            <FilterSelect value={site} onChange={setSite} all="All sites" options={SITES} testId="filter-site" />
            <FilterSelect value={stage} onChange={setStage} all="All stages" options={stages.map((s) => s.name)} testId="filter-stage" />
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input className="pl-8" placeholder="Search Bill # or site" value={q} onChange={(e) => setQ(e.target.value)} data-testid="input-search-bill" />
            </div>
          </div>
          <p className="px-4 pt-3 text-xs font-semibold uppercase tracking-wider text-slate-500">All RA bills · {filtered.length}</p>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                {["Bill ID", "Site", "Contractor", "Amount", "Stage", "Days"].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider">{h}</TableHead>)}
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((b) => (
                  <TableRow key={b.id} onClick={() => setSel(b.id)} data-testid={`row-ra-bill-${b.bill_no}`}
                    className={`row-hover cursor-pointer ${sel === b.id ? "bg-sky-50 hover:bg-sky-50" : ""}`}>
                    <TableCell className="font-mono text-xs font-medium">{b.bill_no}</TableCell>
                    <TableCell className="text-sm">{b.site}</TableCell>
                    <TableCell className="text-sm">{b.contractor}</TableCell>
                    <TableCell className="whitespace-nowrap font-mono text-sm">{inr(b.amount)}</TableCell>
                    <TableCell><StatusBadge value={billStageLabel(b)} /></TableCell>
                    <TableCell className={`text-sm ${b.days > 5 ? "font-semibold text-rose-600" : ""}`}>{b.days}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Panel>
        <div className="min-w-0 lg:col-span-4">
          {selected && stages.length > 0 && <BillPanel bill={selected} stages={stages} user={user} onUpdated={onUpdated} />}
        </div>
      </div>
      <NewBillDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(b) => { setBills((bs) => [b, ...bs]); setSel(b.id); }} />
    </div>
  );
}
